// Piezas compartidas entre Persona y Mascota.
// Fuente unica de verdad: el frontend las usa para UX, el backend como
// autoridad. No reescribir estas reglas en ninguna de las dos capas.

import { z } from "zod";
import { sanitizarEmail, sanitizarTexto } from "../lib/sanitizar.js";

// Los valores deben coincidir exactamente con los enums de prisma/schema.prisma.
export const TIPO_REPORTE = ["DESAPARECIDO", "ENCONTRADO"];
export const ESTADO_REPORTE = ["ACTIVO", "REUNIDO", "CERRADO"];
export const TIPO_MULTIMEDIA = ["FOTO", "VIDEO"];

export const LIMITES = {
  // OJO: este valor es solo una pista para la UX del frontend. La autoridad es
  // IMAGEN_MAX_MB en el entorno del backend, y se consulta en tiempo real con
  // GET /api/:entidad/:id/multimedia/limites. Si cambias uno, revisa el otro.
  fotoBytes: 3 * 1024 * 1024,
  videoBytes: 15 * 1024 * 1024,
  videoSegundos: 15,
};

export const textoLibre = (max) => z
  .string()
  .transform((valor) => sanitizarTexto(valor))
  .pipe(z.string().min(1).max(max));

/// E.164 sin "+": codigo internacional y numero local, maximo 15 digitos.
export const telefono = z
  .string()
  .trim()
  .regex(/^\d{11,15}$/, "Debe incluir el codigo de pais y un numero de 10 digitos");

// Coordenadas acotadas al territorio continental colombiano: rechaza de entrada
// pines obviamente basura o mal geolocalizados.
export const latitud = z.number().min(-4.3).max(13.5);
export const longitud = z.number().min(-79.1).max(-66.8);

export const punto = z.object({ lat: latitud, lng: longitud });

// Version coercionada para query strings, donde todo llega como texto. En el
// body se usan las de arriba: ahi un "4.53" entre comillas es un error del
// cliente y hay que verlo, no taparlo.
const latitudQuery = z.coerce.number().min(-4.3).max(13.5);
const longitudQuery = z.coerce.number().min(-79.1).max(-66.8);

/// Fecha pasada: nadie se pierde en el futuro. Tolera 5 min de reloj desfasado.
export const fechaPasada = z.coerce
  .date()
  .refine((d) => d.getTime() <= Date.now() + 5 * 60 * 1000, {
    message: "La fecha no puede estar en el futuro",
  });

export const reportanteSchema = z.object({
  nombreReportante: textoLibre(80),
  telefonoReportante: telefono,
  emailReportante: z
    .string()
    .transform((valor) => sanitizarEmail(valor))
    .pipe(z.email().max(160)),
});

/// 10 caracteres del alfabeto sin I/O/0/1 (ver backend/src/lib/codigoGestion.js).
export const codigoGestionSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-HJ-NP-Z2-9]{10}$/, "Codigo de gestion invalido");

export const cerrarSchema = z.object({
  estado: z.enum(["REUNIDO", "CERRADO"]),
});

export const feedQuerySchema = z.object({
  estado: z.enum(ESTADO_REPORTE).default("ACTIVO"),
  tipo: z.enum(TIPO_REPORTE).optional(),
  // Paginacion por cursor (keyset), nunca OFFSET.
  cursor: z.string().length(36).optional(),
  limite: z.coerce.number().int().min(1).max(50).default(20),
});

export const mapaQuerySchema = z
  .object({
    norte: latitudQuery,
    sur: latitudQuery,
    este: longitudQuery,
    oeste: longitudQuery,
  })
  .refine((v) => v.norte >= v.sur && v.este >= v.oeste, {
    message: "Los limites del mapa estan invertidos",
  });
